import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Users, Package, Clock, CheckCircle, 
  XCircle, AlertTriangle, Award, BarChart3, Calendar, RefreshCw 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const TRCAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [dailySummary, setDailySummary] = useState(null);
  const [engineerPerformance, setEngineerPerformance] = useState([]);
  const [error, setError] = useState('');

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch daily summary
      const dailyRes = await axios.get(`${API_URL}/api/trc/analytics/daily-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch engineer performance
      const performanceRes = await axios.get(`${API_URL}/api/trc/analytics/engineer-performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (dailyRes.data.success) {
        setDailySummary(dailyRes.data);
      }
      
      if (performanceRes.data.success) {
        setEngineerPerformance(performanceRes.data.analytics);
      }
      
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: ZOBOX_BLUE }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const metrics = dailySummary?.metrics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: ZOBOX_BLUE }}>
            TRC Analytics Dashboard
          </h1>
          <p className="text-gray-600 mt-1 flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Daily Performance Report - {dailySummary?.date}
          </p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
          style={{ backgroundColor: ZOBOX_BLUE }}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Today's Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">STNs Created</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{metrics.stns_created || 0}</p>
            </div>
            <Package className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">SRNs Received</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">{metrics.srns_received || 0}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-purple-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Repairs Completed</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{metrics.repairs_completed || 0}</p>
            </div>
            <TrendingUp className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Devices Assigned</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">{metrics.devices_assigned || 0}</p>
            </div>
            <Users className="w-10 h-10 text-orange-500" />
          </div>
        </div>
      </div>

      {/* FQC Performance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold" style={{ color: ZOBOX_BLUE }}>FQC Pass</h3>
            <CheckCircle className="w-6 h-6 text-green-500" />
          </div>
          <p className="text-4xl font-bold text-green-600">{metrics.fqc_pass || 0}</p>
          <p className="text-sm text-gray-600 mt-2">Devices passed quality check</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold" style={{ color: ZOBOX_BLUE }}>FQC Fail (RTR)</h3>
            <XCircle className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-4xl font-bold text-red-600">{metrics.fqc_fail || 0}</p>
          <p className="text-sm text-gray-600 mt-2">Returned to repair</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold" style={{ color: ZOBOX_BLUE }}>Pass Rate</h3>
            <Award className="w-6 h-6" style={{ color: ZOBOX_BLUE }} />
          </div>
          <p className="text-4xl font-bold" style={{ color: ZOBOX_BLUE }}>
            {metrics.fqc_pass > 0 
              ? Math.round((metrics.fqc_pass / (metrics.fqc_pass + metrics.fqc_fail)) * 100)
              : 0}%
          </p>
          <p className="text-sm text-gray-600 mt-2">Overall quality rate</p>
        </div>
      </div>

      {/* Engineer Performance Table */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold" style={{ color: ZOBOX_BLUE }}>
              Engineer Performance
            </h2>
            <div className="flex items-center text-sm text-gray-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              {engineerPerformance.length} Engineers
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Engineer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Assigned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Completed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  In Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  FQC Pass Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Avg Repair Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Capacity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {engineerPerformance.map((engineer, index) => {
                const passRateColor = engineer.fqc_pass_rate >= 80 
                  ? 'text-green-600' 
                  : engineer.fqc_pass_rate >= 60 
                    ? 'text-yellow-600' 
                    : 'text-red-600';
                
                const capacityColor = engineer.capacity_utilization >= 80 
                  ? 'bg-red-500' 
                  : engineer.capacity_utilization >= 60 
                    ? 'bg-yellow-500' 
                    : 'bg-green-500';

                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center font-bold text-white"
                             style={{ backgroundColor: ZOBOX_BLUE }}>
                          {engineer.name.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{engineer.name}</div>
                          <div className="text-sm text-gray-500">{engineer.engineer_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {engineer.total_assigned}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {engineer.completed}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {engineer.in_progress}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm font-bold ${passRateColor}`}>
                        {engineer.fqc_pass_rate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1 text-gray-400" />
                        {engineer.avg_repair_time_minutes > 0 
                          ? `${Math.round(engineer.avg_repair_time_minutes)}m`
                          : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${capacityColor}`}
                            style={{ width: `${engineer.capacity_utilization}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">
                          {engineer.workload}/25
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {engineer.fqc_pass_rate >= 80 ? (
                        <span className="flex items-center text-green-600 text-sm font-medium">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          Excellent
                        </span>
                      ) : engineer.fqc_pass_rate >= 60 ? (
                        <span className="flex items-center text-yellow-600 text-sm font-medium">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          Good
                        </span>
                      ) : (
                        <span className="flex items-center text-red-600 text-sm font-medium">
                          <TrendingDown className="w-4 h-4 mr-1" />
                          Needs Improvement
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {engineerPerformance.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Users className="w-16 h-16 mx-auto mb-3 text-gray-300" />
            <p>No engineer data available</p>
          </div>
        )}
      </div>

      {/* Performance Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
          <h3 className="text-lg font-bold mb-3" style={{ color: ZOBOX_BLUE }}>
            Top Performers
          </h3>
          <div className="space-y-3">
            {engineerPerformance.slice(0, 3).map((eng, idx) => (
              <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                <div className="flex items-center">
                  <Award className={`w-5 h-5 mr-2 ${
                    idx === 0 ? 'text-yellow-500' : idx === 1 ? 'text-gray-400' : 'text-orange-400'
                  }`} />
                  <span className="font-medium">{eng.name}</span>
                </div>
                <span className="font-bold" style={{ color: ZOBOX_BLUE }}>
                  {eng.fqc_pass_rate}%
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-lg border border-orange-200">
          <h3 className="text-lg font-bold mb-3 text-orange-700">
            Needs Attention
          </h3>
          <div className="space-y-3">
            {engineerPerformance
              .filter(eng => eng.fqc_pass_rate < 60 || eng.capacity_utilization > 80)
              .slice(0, 3)
              .map((eng, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-orange-500" />
                    <span className="font-medium">{eng.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-orange-700">
                      {eng.fqc_pass_rate}% pass rate
                    </div>
                    <div className="text-xs text-gray-600">
                      {eng.capacity_utilization}% capacity
                    </div>
                  </div>
                </div>
              ))}
            {engineerPerformance.filter(eng => eng.fqc_pass_rate < 60 || eng.capacity_utilization > 80).length === 0 && (
              <div className="text-center text-gray-500 py-4">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p className="text-sm">All engineers performing well!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TRCAnalytics;
